import classnames from 'classnames/bind'
import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useInView } from 'react-intersection-observer'
import { useTranslate } from 'react-polyglot'
import { useIsomorphicLayoutEffect } from 'react-use'

import { useMeasureObserver } from '@unlikelystudio/react-hooks'

import Video from '~/components/Abstracts/Video'
import Image from '~/components/UI/Image'

import { CTA } from '~/data/dictionary'

import BlurredCta from '../BlurredCta'
import { ProductHeaderProps } from '../ProductHeader'
import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface MediaGalleryProps {
  className?: string
  imageIndex?: number
  setImageIndex?: React.Dispatch<any>
  onExitFullscreen?: () => void
  images?: ProductHeaderProps['media'][]
}

interface GalleryItemProps {
  images: ProductHeaderProps['media']
  index: number
  setIndex: MediaGalleryProps['setImageIndex']
}

function GalleryItem({ images, index, setIndex }: GalleryItemProps) {
  const [setRef, inView] = useInView({ threshold: 0.5, initialInView: false })

  useEffect(() => {
    if (inView) {
      setIndex(index)
    }
  }, [inView, setIndex, index])

  return (
    <div ref={setRef} className={css.imageContainer}>
      {images?.map((image, index) => (
        <>
          {image?.video ? (
            <div className={css.video}>
              <Video
                src={image?.video}
                loop
                loading="lazy"
                autoPlay
                playsInline
                muted
                objectFit="cover"
              />
            </div>
          ) : (
            <Image
              layout="fill"
              objectFit="cover"
              className={cx(css.image, { isAbsolute: index > 0 })}
              {...image.image}
            />
          )}
        </>
      ))}
    </div>
  )
}

function MediaGallery({
  className,
  images,
  imageIndex,
  setImageIndex,
  onExitFullscreen,
}: MediaGalleryProps) {
  const t = useTranslate()
  const galleryRef = useRef<HTMLDivElement>()
  const sliderRef = useRef<HTMLDivElement>()
  const { height } = useMeasureObserver(galleryRef)

  useIsomorphicLayoutEffect(() => {
    const $html = document.documentElement
    const $body: HTMLElement = document.querySelector('#__next')
    const scrollTop = $html.scrollTop || $body.scrollTop
    const htmlStyle = { ...$html.style }
    const bodyStyle = { ...$body.style }

    $body.style.top = `-${scrollTop}px`
    $body.style.width = '100%'
    $body.style.height = '100%'
    $body.style.position = 'fixed'
    $body.style.overflow = 'hidden'
    $body.style.visibility = 'hidden'
    $body.style['-webkit-overflow-scrolling'] = 'auto'

    return () => {
      $html.style.height = htmlStyle.height || ''
      $html.style.overflow = htmlStyle.overflow || ''
      ;['top', 'width', 'height', 'overflow', 'position', 'visibility'].forEach(
        (x) => {
          $body.style[x] = bodyStyle[x] || ''
        },
      )

      window.scrollTo(0, scrollTop)
    }
  }, [])

  const initialPositionSet = useRef(false)
  useIsomorphicLayoutEffect(() => {
    if (
      initialPositionSet.current === false &&
      height &&
      imageIndex &&
      images.length > 0
    ) {
      window.scrollTo(0, (height / images.length) * imageIndex)
      initialPositionSet.current = true
    }
  }, [height, imageIndex, images?.length])

  return (
    <>
      {createPortal(
        <div
          onClick={onExitFullscreen}
          ref={galleryRef}
          className={cx(css.MediaGallery, className)}>
          <div ref={sliderRef} className={css.slider}>
            <GalleryItem
              setIndex={setImageIndex}
              index={imageIndex}
              images={images?.[imageIndex]}
            />
          </div>
          <BlurredCta
            onClick={onExitFullscreen}
            className={cx(css.closeButton)}>
            {t(CTA.CLOSE)}
          </BlurredCta>
        </div>,
        document.body,
      )}
    </>
  )
}

MediaGallery.defaultProps = {}

export default MediaGallery
